import {
  Component,
  ElementRef,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from "@angular/core";
import { BatteryType } from "src/app/services/event.service";

export interface DutIndividualData {
  icon?: string;
  dataValue: string;
}

export interface IndividualTableData {
  test: string;
  analytics: string;
  unit: string;
  result: string;
}

export interface DutDetailsData {
  "Test ID": DutIndividualData;
  "Test Date": DutIndividualData;
  "Test Rig": DutIndividualData;
  "Battery Unique ID": DutIndividualData;
  "BMS Unique ID": DutIndividualData;
  Product: DutIndividualData;
  "Variant (assigned during EOL)": DutIndividualData;
  "Hardware Version": DutIndividualData;
  "Rated capacity": DutIndividualData;
  "Nominal voltage": DutIndividualData;
}

export interface Page1TableData {
  [key: string]: any;
}

export interface Page2TableData {
  [key: string]: any;
}

export interface SummaryDataValue {
  dataValue: string;
}

export interface SummaryData {
  "EOL + BMS IPQC Result": SummaryDataValue;
  "EOL Result": SummaryDataValue;
  "BMS IPQC Result": SummaryDataValue;
  Pretests: SummaryDataValue;
  Calibration: SummaryDataValue;
  Discharge: SummaryDataValue;
  Charge: SummaryDataValue;
  "OEM Storage": SummaryDataValue;
}

@Component({
  selector: "app-epack-pdf-report",
  templateUrl: "./epack-pdf-report.component.html",
  styleUrls: ["./epack-pdf-report.component.css"],
  encapsulation: ViewEncapsulation.None,
})
export class EpackPdfReportComponent implements OnInit {
  constructor() {}

  ecalLogo = "assets/img/epack-pdf-report/enginecal_logo.png";
  expoLogo = "assets/img/epack-pdf-report/exponent_logo.png";
  expoLogoBlack = "assets/img/epack-pdf-report/exponent_logo_black.png";

  @ViewChild("page2Temp") page2Temp: ElementRef<HTMLDivElement>;
  ngAfterViewInit(): void {}

  ngOnInit(): void {}

  downloadReport(reportData: {
    dutDetailsData: Record<string, any>;
    summaryData: Record<string, any>;
    page1TableData: Record<string, IndividualTableData[]>;
    page2TableData: Record<string, IndividualTableData[]>;
    battery_type: BatteryType;
  }) {
    const {
      dutDetailsData,
      page1TableData,
      page2TableData,
      summaryData,
      battery_type,
    } = reportData;
    const docWindow: any = window;
    const { jsPDF } = docWindow.jspdf;

    const element = document.getElementById("pdfReportUI") as HTMLDivElement;
    element.style.display = "block";

    this.clearPdfPageUI();
    this.generatePage1UI(dutDetailsData, summaryData);
    this.generatePage1TableUI(page1TableData);
    this.generatePage2UI(page2TableData);

    const pdf = new jsPDF({
      orientation: "portrait",
      unit: "px",
      // format: "a4",
      scale: 0.1,
      format: [720, 1092],
    });

    pdf.html(element, {
      callback: function (pdf) {
        element.style.display = "none";
        // const testId: any = dutDetailsData["Test ID"];
        // const fileName = `epack_test_report_${testId}.pdf`;
        // pdf.save(fileName);

        // Save the generated PDF as a Blob and open it in a new window for preview
        const pdfBlob = pdf.output("blob");
        const pdfUrl = URL.createObjectURL(pdfBlob);
        window.open(pdfUrl, "_blank");
      },
      x: 10,
      y: 10,
    });
  }

  generatePage1UI(
    dutDetailsData: Record<string, any>,
    summaryData: Record<string, any>,
  ) {
    // --------------------------------------------------------
    // ------------- DUT Details ----------------------------------
    // --------------------------------------------------------

    const DUTDetailsContainer = document.getElementById("DUTDetailsContainer");

    Object.entries(dutDetailsData).forEach(([key, value]) => {
      const paragaph = document.createElement("p");
      // const span = document.createElement("span");
      // const matIcon = document.createElement("mat-icon");

      // matIcon.classList.add("material-icons");
      // matIcon.innerText = icon;
      paragaph.innerHTML = `<b style="font-weight: 600; opacity: 0.8">${key}:</b> ${value}`;

      // paragaph.appendChild(matIcon);
      // paragaph.appendChild(span);
      DUTDetailsContainer.appendChild(paragaph);
    });

    // manage borders
    // const DUTDetailsCount = DUTDetailsContainer.childElementCount;

    // if (DUTDetailsCount > 3) {
    //   const rowGroupingCount = 3;
    //   const rowCount = Math.ceil(DUTDetailsCount / rowGroupingCount);

    //   DUTDetailsContainer.childNodes.forEach(
    //     (currentChild: HTMLElement, index) => {
    //       const currentIndex = index + 1;
    //       const isLastRow =
    //         currentIndex > rowCount * rowGroupingCount - rowGroupingCount;
    //       const detailExistBelow = DUTDetailsContainer.childNodes[
    //         index + rowGroupingCount
    //       ]
    //         ? true
    //         : false;

    //       if (!isLastRow) {
    //         currentChild.style.borderBottom = "none";
    //         if (currentIndex % rowGroupingCount !== 0) {
    //           currentChild.style.borderRight = "none";
    //         }

    //         if (!detailExistBelow) {
    //           currentChild.style.borderBottom = "1px solid black";
    //         }
    //       }
    //     }
    //   );
    // }

    // --------------------------------------------------------
    // ------------- Summary ----------------------------------
    // --------------------------------------------------------

    const summaryContainer = document.getElementById("summaryContainer");

    Object.entries(summaryData).forEach(([key, value]) => {
      const { dataValue } = value;
      const summaryDiv = document.createElement("div");
      const summaryHeading = document.createElement("h3");
      const sumaryValueDiv = document.createElement("div");
      const summaryValue = document.createElement("h4");

      summaryHeading.innerText = key;
      summaryValue.innerText = dataValue;

      summaryHeading.classList.add("reportHeaderText");
      summaryValue.classList.add("valueText");

      if (String(dataValue).toLowerCase() === "fail") {
        summaryDiv.classList.add("failingSummary");
        summaryHeading.classList.add("failingSummaryHeading");
        summaryValue.classList.add("failingSummaryValue");
      }

      summaryDiv.appendChild(summaryHeading);
      summaryDiv.appendChild(sumaryValueDiv);
      sumaryValueDiv.appendChild(summaryValue);

      summaryContainer.appendChild(summaryDiv);
    });
  }

  generatePage1TableUI(tableData: Record<string, IndividualTableData[]>) {
    const tableContainer = document.getElementById("page1Table");

    // Create table headers
    const tableHeaderContainer = document.createElement("tr");
    ["Group", "Test", "Analytics", "Unit(s)", "Result"].forEach((header) => {
      const p = document.createElement("th");
      p.innerText = header;
      tableHeaderContainer.appendChild(p);
    });

    tableContainer.appendChild(tableHeaderContainer);

    // create table rows
    Object.entries(tableData).forEach(([group, data]) => {
      const currentGroupRow = document.createElement("tr");

      // setting rowspan for group name
      const mainTd = document.createElement("td");
      mainTd.rowSpan = data.length;
      mainTd.innerHTML = group;
      currentGroupRow.appendChild(mainTd);

      // create table rows
      data.forEach(({ analytics, result, test, unit }, index) => {
        // create elements for table
        const tr = document.createElement("tr");
        const testRow = document.createElement("td");
        const analyticsRow = document.createElement("td");
        const unitRow = document.createElement("td");
        const resultRow = document.createElement("td");

        if (String(result).toLowerCase().trim() === "fail") {
          resultRow.style.color = "#b90000";
          resultRow.style.backgroundColor = "#F5C1C3";
        }

        // adding data to the rows
        testRow.innerText = test;
        analyticsRow.innerText = analytics;
        unitRow.innerHTML = unit;
        resultRow.innerText = result;

        // appending elements to table rowk
        if (index === 0) {
          currentGroupRow.appendChild(testRow);
          currentGroupRow.appendChild(analyticsRow);
          currentGroupRow.appendChild(unitRow);
          currentGroupRow.appendChild(resultRow);
          tableContainer.appendChild(currentGroupRow);
        } else {
          tr.appendChild(testRow);
          tr.appendChild(analyticsRow);
          tr.appendChild(unitRow);
          tr.appendChild(resultRow);
          tableContainer.appendChild(tr);
        }
      });
    });
  }

  generatePage2UI(tableData: Record<string, IndividualTableData[]>) {
    const tableContainer = document.getElementById("page2Table");

    // Create table headers
    const tableHeaderContainer = document.createElement("tr");
    ["Group", "Test", "Analytics", "Unit(s)", "Result"].forEach((header) => {
      const p = document.createElement("th");
      p.innerText = header;
      tableHeaderContainer.appendChild(p);
    });

    tableContainer.appendChild(tableHeaderContainer);

    // create table rows
    Object.entries(tableData).forEach(([group, data]) => {
      const currentGroupRow = document.createElement("tr");

      // setting rowspan for group name
      const mainTd = document.createElement("td");
      mainTd.rowSpan = data.length;
      mainTd.innerHTML = group;
      currentGroupRow.appendChild(mainTd);

      // create table rows
      data.forEach(({ analytics, result, test, unit }, index) => {
        // create elements for table
        const tr = document.createElement("tr");
        const testRow = document.createElement("td");
        const analyticsRow = document.createElement("td");
        const unitRow = document.createElement("td");
        const resultRow = document.createElement("td");

        if (String(result).toLowerCase().trim() === "fail") {
          resultRow.style.color = "#b90000";
          resultRow.style.backgroundColor = "#F5C1C3";
        }

        // adding data to the rows
        testRow.innerText = test;
        analyticsRow.innerText = analytics;
        unitRow.innerText = unit;
        resultRow.innerText = result;

        // appending elements to table rowk
        if (index === 0) {
          currentGroupRow.appendChild(testRow);
          currentGroupRow.appendChild(analyticsRow);
          currentGroupRow.appendChild(unitRow);
          currentGroupRow.appendChild(resultRow);
          tableContainer.appendChild(currentGroupRow);
        } else {
          tr.appendChild(testRow);
          tr.appendChild(analyticsRow);
          tr.appendChild(unitRow);
          tr.appendChild(resultRow);
          tableContainer.appendChild(tr);
        }
      });
    });
  }

  clearPdfPageUI() {
    const elements = [
      document.getElementById("DUTDetailsContainer"),
      document.getElementById("summaryContainer"),
      document.getElementById("page1Table"),
      document.getElementById("page2Table"),
    ];

    this.page2Temp.nativeElement.style.display = "";

    elements.forEach((element) => {
      while (element.firstChild) {
        element.removeChild(element.firstChild);
      }
    });
  }
}
